import type { ReactNode } from 'react';
import { Code } from '../CodeSample';
import { GuideHeader } from './GuideHeader';

const headers = `RateLimit-Limit: 1200
RateLimit-Remaining: 1187
RateLimit-Reset: 42
RateLimit-Policy: 1200;w=60`;

const clients = `# TypeScript types for every operation and shape
npx openapi-typescript https://v2.paige.app/openapi/paige-v1.public.yaml -o paige.d.ts

# A C# client (NSwag; the generated code uses Newtonsoft.Json)
nswag openapi2csclient /input:https://v2.paige.app/openapi/paige-v1.public.yaml /classname:PaigeClient /namespace:Paige.Client /output:PaigeClient.cs`;

/** The rate limit, uploads, credits, retention, versions and typed clients. */
export function LimitsGuide(): ReactNode {
  return (
    <article className="docs-page">
      <GuideHeader slug="limits" />
      <RateLimit />
      <Sizes />
      <Versions />
    </article>
  );
}

function RateLimit(): ReactNode {
  return (
    <section className="docs-section" aria-labelledby="rate-title">
      <h2 id="rate-title">The rate limit</h2>
      <p>
        Each API key may make 1,200 requests a minute, counted across Paige, in windows that start
        on the minute. Every answer to a key says where it stands; past the limit the answer is 429{' '}
        <code>rate_limited</code> with <code>Retry-After</code>, and nothing was done. Polling a
        batch every few seconds is far inside the limit; for many batches at once, the event stream
        or a webhook is kinder than polling each.
      </p>
      <Code code={headers} label="the rate limit headers" />
    </section>
  );
}

function Sizes(): ReactNode {
  return (
    <section className="docs-section" aria-labelledby="sizes-title">
      <h2 id="sizes-title">Uploads, pages and how long files are kept</h2>
      <ul className="docs-list">
        <li>
          <strong>Files:</strong> PDF, TIFF, PNG or JPEG, up to 30 MB in one request (30,000,000
          bytes), so one file of up to 29,900,000 bytes; send more files as more batches.
        </li>
        <li>
          <strong>Pages:</strong> reading uses the company’s pages by what each page actually takes.{' '}
          <code>GET /company/billing</code> has the balance. At zero, uploads are still accepted and
          their documents wait until pages arrive; Paige never reads past zero.
        </li>
        <li>
          <strong>Plans:</strong> the API is included with paid plans. On Free every call with a key
          is 403 <code>paid_plan_required</code> until an admin chooses a plan.
        </li>
        <li>
          <strong>Retention:</strong> a job keeps an exported document’s files 30 days unless its
          retention says otherwise (<code>PATCH /jobs/{'{jobId}'}/settings</code>: 7 days, 30 days,
          a year, or forever). After that the document and its fields stay, with{' '}
          <code>purgedAt</code> set, and its package is gone: download what you keep.
        </li>
      </ul>
    </section>
  );
}

function Versions(): ReactNode {
  return (
    <section className="docs-section" aria-labelledby="versions-title">
      <h2 id="versions-title">Versions and typed clients</h2>
      <p>
        The version is in the path: <code>/v1</code>. Within it, changes only add: new operations,
        new optional request fields, new fields in answers, new values in an open list such as an
        error <code>code</code>. Ignore what you do not know and your integration keeps working. A
        change that would break a working integration comes as <code>/v2</code>, with{' '}
        <code>/v1</code> running beside it. A field marked deprecated in the reference stays in v1.
      </p>
      <p>
        The reference is an OpenAPI 3.0 document at{' '}
        <a href="/openapi/paige-v1.public.yaml" download>
          /openapi/paige-v1.public.yaml
        </a>
        , so your tools can give you a typed client:
      </p>
      <Code code={clients} label="the client generators" />
    </section>
  );
}
